<!--
  Lets a form component (ModalTask.vue, FieldLensRequestWizard.vue) render either
  as its normal popup Dialog (existing dashboard usage, unchanged) or as an inline
  card in the chat conversation (matching BoostPrompt.vue/ToolFormPrompt.vue),
  controlled by a single `inline` prop — so there is one implementation of the
  form, not two.

  The `header`/default slots are declared once here and Vue only evaluates them
  once per render regardless of which branch is active, so nothing about the
  form's own markup needs to be duplicated to support both modes.
-->
<template>
  <Dialog v-if="!inline" v-model="visible" :size="size" :closable @close="emit('close')">
    <template #header>
      <slot name="header" />
    </template>
    <slot />
  </Dialog>

  <div v-else class="flex w-full justify-start">
    <div class="flex w-full max-w-[92%] gap-3 sm:max-w-[80%]">
      <div
        class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
        <Icon :icon="icon" class="size-5" />
      </div>
      <div
        class="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-iris-200 bg-iris-50/80 px-5 py-4 shadow-sm dark:border-iris-800 dark:bg-iris-950/30">
        <div v-if="$slots.header" class="mb-3">
          <slot name="header" />
        </div>
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@iconify/vue'
import Dialog from '@/components/Core/Dialog.vue'

defineProps({
  inline: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'xlarge',
  },
  icon: {
    type: String,
    default: 'heroicons:bolt',
  },
  // Dialog mode shows the header 'X' (PrimeVue also only honors Escape when closable)
  closable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

// Only meaningful in Dialog mode — inline mode has no open/close transition of
// its own, the caller controls mounting with v-if instead.
const visible = defineModel()
</script>
